import React, { useState, useEffect } from 'react';

// Lead field mappings that correspond to Lead model columns
type LeadFieldMapping = 'first_name' | 'last_name' | 'phone_number' | 'company_name' | 'job_title' | 'city' | 'state' | 'country';

// Custom field type definition
interface PortalCustomField {
  id: string;
  type: 'email' | 'text' | 'phone' | 'number' | 'textarea' | 'select' | 'checkbox' | 'date' | 'url';
  label: string;
  required: boolean;
  position: number;
  options?: string[];
  placeholder?: string;
  default_value?: string;
  lead_field?: LeadFieldMapping;
}

interface PortalProps {
  isOpen: boolean;
  onClose: () => void;
  siteName?: string;
  logoUrl?: string;
  accentColor?: string;
  headline?: string;
  description?: string;
  buttonText?: string;
  buttonUrl?: string;
  footerText?: string;
  successMessage?: string;
  skipAnimation?: boolean;
  featureImageUrl?: string;
  customFields?: PortalCustomField[];
  funnelId?: string;
  showLabels?: boolean;
}

const Portal: React.FC<PortalProps> = ({ 
  isOpen, 
  onClose, 
  siteName,
  logoUrl,
  accentColor = '#00BD7C',
  headline,
  description,
  buttonText,
  buttonUrl,
  footerText,
  successMessage,
  skipAnimation = false,
  featureImageUrl,
  customFields = [],
  funnelId,
  showLabels = true,
}) => {
  const [email, setEmail] = useState('');
  const [formData, setFormData] = useState<Record<string, string | boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Initialize form data with default values when custom fields change
  useEffect(() => {
    const initialData: Record<string, string | boolean> = {};
    customFields.forEach(field => {
      if (field.type === 'checkbox') {
        initialData[field.id] = field.default_value === 'true';
      } else {
        initialData[field.id] = field.default_value || '';
      }
    });
    setFormData(initialData);
  }, [customFields]);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsAnimatingOut(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (skipAnimation) return;
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsAnimatingOut(false);
      setIsSubmitted(false);
      setEmail('');
      setFormData({});
      setFormError(null);
      onClose();
    }, 300);
  };

  if (!isVisible && !isOpen) return null;
  
  const shouldAnimate = !skipAnimation;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError(null);

    // Validate required fields
    for (const field of customFields) {
      if (field.required) {
        if (field.type === 'email') {
          if (!email || !email.trim()) {
            setFormError(`${field.label || 'Email'} is required`);
            setIsLoading(false);
            return;
          }
          continue;
        }
        
        const value = formData[field.id];
        if (field.type === 'checkbox') {
          if (!value) {
            setFormError(`${field.label} is required`);
            setIsLoading(false);
            return;
          }
        } else if (!value || (typeof value === 'string' && !value.trim())) {
          setFormError(`${field.label} is required`);
          setIsLoading(false);
          return;
        }
      }
    }

    // Build submission data
    const submissionData: Record<string, any> = {};
    customFields.forEach(field => {
      submissionData[field.id] = formData[field.id];
    });

    try {
      if (funnelId) {
        const response = await fetch(`/api/funnels/${funnelId}/portal_submissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            portal_submission: { email, data: submissionData }
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Submission failed');
        }
      } else {
        console.log('Lead captured (preview):', { email, ...submissionData });
        await new Promise(resolve => setTimeout(resolve, 800));
      }

      setIsLoading(false);
      setIsSubmitted(true);

      if (buttonUrl) {
        setTimeout(() => { window.location.href = buttonUrl; }, 1500);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setFormError(error instanceof Error ? error.message : 'Failed to submit. Please try again.');
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const hasFeaturedImage = !!featureImageUrl;

  return (
    <>
      <style>{`
        @keyframes portalFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes portalFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes portalSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes portalSlideDown {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(20px); }
        }
        @keyframes checkmark {
          0% { stroke-dashoffset: 24; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes scaleIn {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        .portal-input-modern {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .portal-input-modern:focus {
          border-color: ${accentColor};
          box-shadow: 0 0 0 4px ${accentColor}15;
        }
        .portal-input-modern::placeholder {
          color: #a3a3a3;
        }
        .portal-btn-modern {
          background: ${accentColor};
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .portal-btn-modern:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px ${accentColor}40;
        }
        .portal-btn-modern:active:not(:disabled) {
          transform: translateY(0);
        }
        .portal-btn-modern:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        @media (max-width: 768px) {
          .portal-container-modern {
            flex-direction: column !important;
            max-height: 90vh !important;
            max-width: 100% !important;
            margin: 16px !important;
            border-radius: 24px !important;
          }
          .portal-image-modern {
            display: none !important;
          }
          .portal-content-modern {
            padding: 40px 28px !important;
          }
        }
      `}</style>
      
      <div 
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '24px',
          ...(shouldAnimate && {
            animation: `${isAnimatingOut ? 'portalFadeOut' : 'portalFadeIn'} 0.3s ease-out forwards`,
          }),
        }}
      >
        <div 
          className="portal-container-modern"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: hasFeaturedImage ? '32px' : '28px',
            width: '100%',
            maxWidth: hasFeaturedImage ? '920px' : '440px',
            position: 'relative',
            boxShadow: '0 40px 80px -20px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'row',
            overflow: 'hidden',
            pointerEvents: skipAnimation ? 'none' : 'auto',
            ...(shouldAnimate && {
              animation: `${isAnimatingOut ? 'portalSlideDown' : 'portalSlideUp'} 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
            }),
          }}
        >
          {/* Content Section */}
          <div 
            className="portal-content-modern"
            style={{
              flex: hasFeaturedImage ? '0 0 50%' : '1',
              padding: hasFeaturedImage ? '56px 48px' : '48px 40px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: '24px',
                right: '24px',
                background: 'transparent',
                border: 'none',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                color: '#9ca3af',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#9ca3af';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>

            {isSubmitted ? (
              /* Success State */
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 28px',
                  animation: 'scaleIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards',
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12l5 5L20 7" style={{ strokeDasharray: 24, animation: 'checkmark 0.4s ease-out 0.2s forwards', strokeDashoffset: 24 }}/>
                  </svg>
                </div>
                <h2 style={{
                  fontSize: '26px',
                  fontWeight: '600',
                  color: '#111827',
                  marginBottom: '12px',
                  letterSpacing: '-0.02em',
                }}>
                  {successMessage || "You're in!"}
                </h2>
                <p style={{
                  fontSize: '16px',
                  color: '#6b7280',
                  marginBottom: '32px',
                  lineHeight: '1.6',
                }}>
                  Thanks for signing up. We'll be in touch soon.
                </p>
                <button
                  onClick={handleClose}
                  style={{
                    padding: '14px 32px',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#374151',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                >
                  Done
                </button>
              </div>
            ) : (
              /* Form State */
              <>
                {/* Logo */}
                {(logoUrl || siteName) && (
                  <div style={{ marginBottom: '32px' }}>
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt={siteName || ''} 
                        style={{ 
                          maxWidth: '140px', 
                          maxHeight: '40px', 
                          objectFit: 'contain',
                        }} 
                      />
                    ) : (
                      <span style={{ 
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#374151',
                        letterSpacing: '-0.01em',
                      }}>
                        {siteName}
                      </span>
                    )}
                  </div>
                )}

                {/* Headline */}
                <h1 style={{ 
                  fontSize: '32px', 
                  fontWeight: '600',
                  marginBottom: description ? '16px' : '32px',
                  color: '#111827',
                  letterSpacing: '-0.025em',
                  lineHeight: '1.2',
                }}>
                  {headline || 'Join the waitlist'}
                </h1>
                
                {description && (
                  <p style={{
                    fontSize: '17px',
                    color: '#6b7280',
                    marginBottom: '32px',
                    lineHeight: '1.6',
                  }}>
                    {description}
                  </p>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  {customFields
                    .sort((a, b) => a.position - b.position)
                    .map((field) => (
                      <div key={field.id} style={{ marginBottom: '16px' }}>
                        {field.type === 'email' ? (
                          <>
                            {showLabels && field.label && (
                              <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '8px',
                              }}>
                                {field.label}
                              </label>
                            )}
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder={field.placeholder || 'you@example.com'}
                              required
                              className="portal-input-modern"
                              style={{
                                width: '100%',
                                padding: '16px 18px',
                                fontSize: '16px',
                                border: '1.5px solid #e5e7eb',
                                borderRadius: '14px',
                                outline: 'none',
                                boxSizing: 'border-box',
                                backgroundColor: '#fafafa',
                              }}
                            />
                          </>
                        ) : field.type === 'checkbox' ? (
                          <label style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            cursor: 'pointer',
                            padding: '4px 0',
                          }}>
                            <div style={{
                              width: '22px',
                              height: '22px',
                              borderRadius: '6px',
                              border: `2px solid ${formData[field.id] ? accentColor : '#d1d5db'}`,
                              backgroundColor: formData[field.id] ? accentColor : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              flexShrink: 0,
                              marginTop: '1px',
                            }}>
                              {formData[field.id] && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
                                  <path d="M5 12l5 5L20 7"/>
                                </svg>
                              )}
                            </div>
                            <input
                              type="checkbox"
                              checked={!!formData[field.id]}
                              onChange={(e) => setFormData(prev => ({ ...prev, [field.id]: e.target.checked }))}
                              required={field.required}
                              style={{ display: 'none' }}
                            />
                            <span style={{
                              fontSize: '15px',
                              color: '#374151',
                              lineHeight: '1.5',
                            }}>
                              {field.label}
                            </span>
                          </label>
                        ) : field.type === 'textarea' ? (
                          <>
                            {showLabels && field.label && (
                              <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '8px',
                              }}>
                                {field.label}
                              </label>
                            )}
                            <textarea
                              value={(formData[field.id] as string) || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
                              placeholder={field.placeholder || ''}
                              required={field.required}
                              rows={3}
                              className="portal-input-modern"
                              style={{
                                width: '100%',
                                padding: '14px 18px',
                                fontSize: '16px',
                                border: '1.5px solid #e5e7eb',
                                borderRadius: '14px',
                                outline: 'none',
                                boxSizing: 'border-box',
                                backgroundColor: '#fafafa',
                                resize: 'none',
                                minHeight: '100px',
                              }}
                            />
                          </>
                        ) : field.type === 'select' ? (
                          <>
                            {showLabels && field.label && (
                              <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '8px',
                              }}>
                                {field.label}
                              </label>
                            )}
                            <select
                              value={(formData[field.id] as string) || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
                              required={field.required}
                              className="portal-input-modern"
                              style={{
                                width: '100%',
                                padding: '16px 18px',
                                fontSize: '16px',
                                border: '1.5px solid #e5e7eb',
                                borderRadius: '14px',
                                outline: 'none',
                                boxSizing: 'border-box',
                                backgroundColor: '#fafafa',
                                cursor: 'pointer',
                                appearance: 'none',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 16px center',
                              }}
                            >
                              <option value="">{field.placeholder || 'Select...'}</option>
                              {field.options?.map((option, idx) => (
                                <option key={idx} value={option}>{option}</option>
                              ))}
                            </select>
                          </>
                        ) : (
                          <>
                            {showLabels && field.label && (
                              <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '8px',
                              }}>
                                {field.label}
                              </label>
                            )}
                            <input
                              type={field.type === 'phone' ? 'tel' : field.type}
                              value={(formData[field.id] as string) || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
                              placeholder={field.placeholder || ''}
                              required={field.required}
                              className="portal-input-modern"
                              style={{
                                width: '100%',
                                padding: '16px 18px',
                                fontSize: '16px',
                                border: '1.5px solid #e5e7eb',
                                borderRadius: '14px',
                                outline: 'none',
                                boxSizing: 'border-box',
                                backgroundColor: '#fafafa',
                              }}
                            />
                          </>
                        )}
                      </div>
                    ))}

                  {/* Error message */}
                  {formError && (
                    <div style={{
                      padding: '14px 16px',
                      marginBottom: '16px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '12px',
                      color: '#dc2626',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 8v4m0 4h.01"/>
                      </svg>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="portal-btn-modern"
                    style={{
                      width: '100%',
                      padding: '18px 24px',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'white',
                      border: 'none',
                      borderRadius: '14px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                    }}
                  >
                    {isLoading ? (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.3"/>
                          <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                        <span>Please wait...</span>
                      </>
                    ) : (
                      <span>{buttonText || 'Get early access'}</span>
                    )}
                  </button>
                </form>

                {/* Footer */}
                {footerText && (
                  <p style={{
                    fontSize: '13px',
                    color: '#9ca3af',
                    marginTop: '20px',
                    textAlign: 'center',
                  }}>
                    {footerText}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Image Section */}
          {hasFeaturedImage && (
            <div 
              className="portal-image-modern"
              style={{
                flex: '0 0 50%',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <img 
                src={featureImageUrl} 
                alt="" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {/* Subtle gradient overlay */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to right, rgba(255,255,255,0.1), transparent)',
                pointerEvents: 'none',
              }}/>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Portal;
